/**
 * PrepareDocumentModal
 *
 * Steps:
 *   1 — Sender configures up to 4 parties (Signer/Reviewer) + places fields
 *       for each party, for "My Part" (sender), or as a Prefill field
 *       (filled by the sender before sending, e.g. Company Name).
 *   2 — If the document has Prefill fields, fill those in first.
 *   3 — Enter each party's email + name, then send.
 *
 * mode='send'     → place fields (or use template fields) then send
 * mode='template' → place fields and save to document, no send
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PenLine, Type, Calendar, X, ChevronRight, Send, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { documentsAPI, esignAPI } from '@/lib/api';

// ── PDF.js loader ─────────────────────────────────────────────────────────────

function loadPdfJs() {
  return new Promise((resolve) => {
    if (window.pdfjsLib) { resolve(window.pdfjsLib); return; }
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      resolve(window.pdfjsLib);
    };
    document.head.appendChild(s);
  });
}

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const MAX_PARTIES = 10;

// ── Field config ──────────────────────────────────────────────────────────────

const FIELD_TYPES = [
  { type: 'signature', label: 'Signature', Icon: PenLine,  color: '#A62B1F', bg: '#F8ECE9', w: 28, h: 8  },
  { type: 'text',      label: 'Text',      Icon: Type,     color: '#3B82F6', bg: '#EFF6FF', w: 28, h: 5  },
  { type: 'date',      label: 'Date',      Icon: Calendar, color: '#10B981', bg: '#ECFDF5', w: 18, h: 5  },
];
const fieldCfg = Object.fromEntries(FIELD_TYPES.map(f => [f.type, f]));

const PARTY_COLORS = ['#A62B1F', '#4C6B82', '#2F6B6B', '#EC4899', '#10B981', '#EAB308', '#EF4444', '#06B6D4', '#A855F7', '#84CC16'];
const SENDER_COLOR = '#F97316';
const PREFILL_COLOR = '#F59E0B';

function targetKey(target) {
  return target.kind === 'party' ? `party:${target.index}` : target.kind;
}
function targetsEqual(a, b) {
  return targetKey(a) === targetKey(b);
}
function targetColor(target, parties) {
  if (target.kind === 'sender') return SENDER_COLOR;
  if (target.kind === 'prefill') return PREFILL_COLOR;
  return PARTY_COLORS[target.index % PARTY_COLORS.length];
}
function targetLabel(target, parties) {
  if (target.kind === 'sender') return 'My Part';
  if (target.kind === 'prefill') return 'Fill Before Sending';
  const party = parties.find(p => p.index === target.index);
  const n = target.index + 1;
  if (!party) return `Party ${n}`;
  return party.email ? `${party.email}` : `Party ${n} (${party.role === 'reviewer' ? 'Reviewer' : 'Signer'})`;
}

// ── Derive party slots from existing template fields (send-from-template) ─────

function derivePartiesFromFields(fields) {
  const seen = new Map();
  (fields || []).forEach(f => {
    if ((f.role === 'signer' || f.role === 'reviewer') && f.party_index != null && !seen.has(f.party_index)) {
      seen.set(f.party_index, { index: f.party_index, role: f.role, email: '', name: '' });
    }
  });
  return Array.from(seen.values()).sort((a, b) => a.index - b.index);
}

export default function PrepareDocumentModal({ doc, onClose, onSent, mode = 'send' }) {
  const isTemplate = mode === 'template';
  const skipFieldPlacement = mode === 'send' && doc.is_template;

  const initialFields = skipFieldPlacement
    ? (doc.template_fields || []).map(f => ({
        ...f,
        target: f.role === 'sender' ? { kind: 'sender' }
          : f.role === 'prefill' ? { kind: 'prefill' }
          : { kind: 'party', index: f.party_index ?? 0 },
      }))
    : [];
  const initialNeedsPrefill = skipFieldPlacement && initialFields.some(f => f.target.kind === 'prefill' && f.label);

  const [step, setStep] = useState(skipFieldPlacement ? (initialNeedsPrefill ? 1.5 : 2) : 1);
  const [activeTool, setActiveTool] = useState('signature');
  const [fields, setFields] = useState(initialFields);
  const [parties, setParties] = useState(() =>
    skipFieldPlacement
      ? (derivePartiesFromFields(doc.template_fields).length
          ? derivePartiesFromFields(doc.template_fields)
          : [{ index: 0, role: 'signer', email: '', name: '' }])
      : [{ index: 0, role: 'signer', email: '', name: '' }]
  );
  const [activeTarget, setActiveTarget] = useState({ kind: 'party', index: 0 });
  const [prefillValues, setPrefillValues] = useState({});
  const [executeSelf, setExecuteSelf] = useState(true);
  const [executorEmail, setExecutorEmail] = useState('');
  const [executorName, setExecutorName] = useState('');
  const [message, setMessage] = useState('');
  const hasSenderFields = fields.some(f => f.target.kind === 'sender');
  const [pages, setPages] = useState([]);
  const [pdfLoading, setPdfLoading] = useState(!skipFieldPlacement);
  const [pdfError, setPdfError] = useState(false);
  const [sending, setSending] = useState(false);

  // ── Load & render PDF ───────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const resp = await documentsAPI.getContent(doc._id);
        if (cancelled) return;
        const arrayBuffer = resp.data;
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const rendered = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.6 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
          rendered.push({ canvas, width: viewport.width, height: viewport.height, pageNum: i });
        }
        if (!cancelled) { setPages(rendered); setPdfLoading(false); }
      } catch (err) {
        if (!cancelled) { setPdfError(true); setPdfLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [doc._id]);

  // ── Party management ────────────────────────────────────────────────────────

  const addParty = () => {
    if (parties.length >= MAX_PARTIES) return;
    const nextIndex = Math.max(-1, ...parties.map(p => p.index)) + 1;
    setParties(prev => [...prev, { index: nextIndex, role: 'signer', email: '', name: '' }]);
    setActiveTarget({ kind: 'party', index: nextIndex });
  };

  const removeParty = (index) => {
    if (parties.length <= 1) return;
    setParties(prev => prev.filter(p => p.index !== index));
    setFields(prev => prev.filter(f => !(f.target.kind === 'party' && f.target.index === index)));
    if (activeTarget.kind === 'party' && activeTarget.index === index) {
      setActiveTarget({ kind: 'party', index: parties[0].index });
    }
  };

  const updateParty = (index, patch) => {
    setParties(prev => prev.map(p => p.index === index ? { ...p, ...patch } : p));
    // Reviewers can still be given a signature/initial field — it's just optional
    // for them to fill, so no need to strip fields when switching to reviewer.
  };

  // ── Place field on click ────────────────────────────────────────────────────

  const pageRefs = useRef({});
  const dragging = useRef(null);
  const didDrag = useRef(false);

  const handlePageClick = useCallback((e, pageNum) => {
    if (didDrag.current) { didDrag.current = false; return; }
    const el = pageRefs.current[pageNum];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x_pct = ((e.clientX - rect.left) / rect.width) * 100;
    const y_pct = ((e.clientY - rect.top) / rect.height) * 100;
    const cfg = fieldCfg[activeTool];
    setFields(prev => [...prev, {
      id: uid(),
      type: activeTool,
      label: cfg.label,
      target: activeTarget,
      page: pageNum,
      x_pct: Math.min(x_pct, 100 - cfg.w),
      y_pct: Math.min(y_pct, 100 - cfg.h),
      w_pct: cfg.w,
      h_pct: cfg.h,
    }]);
  }, [activeTool, activeTarget]);

  const removeField = (id) => setFields(prev => prev.filter(f => f.id !== id));

  // ── Drag: move & resize ─────────────────────────────────────────────────────

  const startDrag = (e, id, dragMode, pageNum) => {
    e.stopPropagation();
    e.preventDefault();
    const field = fields.find(f => f.id === id);
    if (!field) return;
    dragging.current = { id, dragMode, pageNum, startX: e.clientX, startY: e.clientY, origField: { ...field } };
    didDrag.current = false;

    const onMove = (me) => {
      if (!dragging.current) return;
      didDrag.current = true;
      const { id, dragMode, pageNum, startX, startY, origField } = dragging.current;
      const el = pageRefs.current[pageNum];
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dx = ((me.clientX - startX) / rect.width) * 100;
      const dy = ((me.clientY - startY) / rect.height) * 100;
      setFields(prev => prev.map(f => {
        if (f.id !== id) return f;
        if (dragMode === 'move') {
          return { ...f,
            x_pct: Math.max(0, Math.min(origField.x_pct + dx, 100 - f.w_pct)),
            y_pct: Math.max(0, Math.min(origField.y_pct + dy, 100 - f.h_pct)),
          };
        }
        return { ...f,
          w_pct: Math.max(8, Math.min(origField.w_pct + dx, 100 - origField.x_pct)),
          h_pct: Math.max(4, Math.min(origField.h_pct + dy, 100 - origField.y_pct)),
        };
      }));
    };

    const onUp = () => {
      dragging.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // ── Convert local field/target shape → backend shape ───────────────────────

  const toBackendFields = useCallback(() => fields.map(f => {
    const { target, ...rest } = f;
    if (target.kind === 'sender') return { ...rest, role: 'sender' };
    if (target.kind === 'prefill') return { ...rest, role: 'prefill', parameter_name: f.label };
    const party = parties.find(p => p.index === target.index);
    return { ...rest, role: party?.role || 'signer', party_index: target.index };
  }), [fields, parties]);

  // Unique prefill parameter names (by label) that need a value before sending
  const prefillParams = useMemo(() => {
    const names = new Set();
    fields.forEach(f => { if (f.target.kind === 'prefill' && f.label) names.add(f.label); });
    return Array.from(names);
  }, [fields]);

  const needsPrefillStep = !isTemplate && prefillParams.length > 0;

  // ── Save template ───────────────────────────────────────────────────────────

  const handleSaveTemplate = async () => {
    setSending(true);
    try {
      await documentsAPI.saveTemplate(doc._id, toBackendFields());
      toast.success('Saved as template!');
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSending(false);
    }
  };

  // ── Send ────────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    const missing = parties.find(p => !p.email.trim());
    if (missing) { toast.error('Every recipient needs an email'); return; }
    for (const name of prefillParams) {
      if (!prefillValues[name] || !String(prefillValues[name]).trim()) {
        toast.error(`Please fill in "${name}" before sending`);
        return;
      }
    }
    if (hasSenderFields && !executeSelf && !executorEmail.trim()) {
      toast.error('Enter an email for whoever will execute this document');
      return;
    }
    setSending(true);
    try {
      const partyPayload = parties.map(p => ({ email: p.email.trim(), name: p.name.trim() || undefined, role: p.role }));
      const executor = (hasSenderFields && !executeSelf)
        ? { email: executorEmail.trim(), name: executorName.trim() || undefined }
        : undefined;
      await esignAPI.send(doc._id, partyPayload, toBackendFields(), prefillValues, executor, message.trim() || undefined);
      toast.success('Sent for signature!');
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // ── Step navigation for send flow ───────────────────────────────────────────

  const goToDetailsStep = () => {
    if (fields.length === 0 && !pdfError) { toast.error('Place at least one field on the document first'); return; }
    setStep(needsPrefillStep ? 1.5 : 2);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  // Reviewers can optionally sign/initial too — a field placed for them is just
  // never required in order to submit their review.
  const allTargets = [
    ...parties.map(p => ({ kind: 'party', index: p.index })),
    { kind: 'sender' },
    ...(isTemplate || !skipFieldPlacement ? [{ kind: 'prefill' }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center gap-3">
          <PenLine className="w-5 h-5 text-[#A62B1F]" />
          <div>
            <h2 className="font-semibold text-[#1E2A33] text-base leading-tight">
              {isTemplate ? 'Save as Template' : 'Send for Signature'}
            </h2>
            <p className="text-xs text-[#5C6670] mt-0.5">{doc.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {step === 1 && (
            isTemplate ? (
              <Button
                onClick={() => { if (fields.length === 0 && !pdfError) { toast.error('Place at least one field first'); return; } handleSaveTemplate(); }}
                disabled={sending || pdfLoading}
                className="bg-[#A62B1F] hover:bg-[#7a54e0] text-white gap-1.5"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {sending ? 'Saving…' : 'Save as Template'}
              </Button>
            ) : (
              <Button onClick={goToDetailsStep} className="bg-[#A62B1F] hover:bg-[#7a54e0] text-white gap-1.5" disabled={pdfLoading}>
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            )
          )}
          {step === 1.5 && (
            <>
              {!skipFieldPlacement && <Button variant="ghost" onClick={() => setStep(1)} className="text-[#5C6670]">← Back</Button>}
              <Button onClick={() => setStep(2)} className="bg-[#A62B1F] hover:bg-[#7a54e0] text-white gap-1.5">
                Next <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="ghost" onClick={() => setStep(needsPrefillStep ? 1.5 : (skipFieldPlacement ? 2 : 1))} className="text-[#5C6670]" disabled={skipFieldPlacement && !needsPrefillStep}>
                ← Back
              </Button>
              <Button onClick={handleSend} disabled={sending} className="bg-[#A62B1F] hover:bg-[#e86d61] text-white gap-1.5">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? 'Sending…' : 'Send for Signature'}
              </Button>
            </>
          )}
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-[#5C6670]">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0">

        {/* ── Step 1: parties + field placement ── */}
        {step === 1 && (
          <>
            <div className="w-64 border-r border-gray-100 flex flex-col flex-shrink-0 bg-gray-50 overflow-y-auto">
              {/* Recipients */}
              <div className="p-4 border-b border-gray-100">
                <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-wide mb-3">
                  {isTemplate ? 'Recipient Slots' : 'Recipients'} ({parties.length}/{MAX_PARTIES})
                </p>
                <div className="space-y-2">
                  {parties.map((p, i) => (
                    <div key={p.index} className="bg-white rounded-lg border border-gray-200 p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold" style={{ color: PARTY_COLORS[p.index % PARTY_COLORS.length] }}>
                          Party {i + 1}
                        </span>
                        {parties.length > 1 && (
                          <button onClick={() => removeParty(p.index)} className="text-[#9AA3AB] hover:text-red-400">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      {!isTemplate && (
                        <>
                          <input
                            type="email"
                            value={p.email}
                            onChange={e => updateParty(p.index, { email: e.target.value })}
                            placeholder="Email"
                            className="w-full text-xs border border-gray-200 rounded px-2 py-1 mb-1 text-[#1E2A33] focus:outline-none focus:border-[#A62B1F]"
                          />
                          <input
                            type="text"
                            value={p.name}
                            onChange={e => updateParty(p.index, { name: e.target.value })}
                            placeholder="Name (optional)"
                            className="w-full text-xs border border-gray-200 rounded px-2 py-1 mb-1.5 text-[#1E2A33] focus:outline-none focus:border-[#A62B1F]"
                          />
                        </>
                      )}
                      <div className="flex rounded-md border border-gray-200 overflow-hidden text-[11px] font-medium">
                        <button
                          onClick={() => updateParty(p.index, { role: 'signer' })}
                          className={`flex-1 py-1 ${p.role === 'signer' ? 'bg-[#1E2A33] text-white' : 'bg-white text-[#5C6670]'}`}
                        >Signer</button>
                        <button
                          onClick={() => updateParty(p.index, { role: 'reviewer' })}
                          className={`flex-1 py-1 ${p.role === 'reviewer' ? 'bg-[#1E2A33] text-white' : 'bg-white text-[#5C6670]'}`}
                        >Reviewer</button>
                      </div>
                    </div>
                  ))}
                </div>
                {parties.length < MAX_PARTIES && (
                  <button
                    onClick={addParty}
                    className="w-full mt-2 flex items-center justify-center gap-1 text-xs font-medium py-1.5 rounded-md border border-dashed border-gray-300 text-[#5C6670] hover:border-[#A62B1F] hover:text-[#A62B1F]"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add recipient
                  </button>
                )}
              </div>

              {/* Field tools */}
              <div className="p-4 border-b border-gray-100">
                <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-wide mb-3">Add Fields</p>
                <div className="space-y-2">
                  {FIELD_TYPES.map(({ type, label, Icon, color, bg }) => (
                    <button
                      key={type}
                      onClick={() => setActiveTool(type)}
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all"
                      style={activeTool === type
                        ? { background: bg, color, border: `1.5px solid ${color}` }
                        : { background: 'white', color: '#5C6670', border: '1.5px solid #E3DDD1' }
                      }
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Target selector */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs font-semibold text-[#5C6670] uppercase tracking-wide mb-2">Placing fields for</p>
                  <div className="space-y-1">
                    {allTargets.map(t => {
                      const active = targetsEqual(t, activeTarget);
                      const color = targetColor(t, parties);
                      return (
                        <button
                          key={targetKey(t)}
                          onClick={() => setActiveTarget(t)}
                          className="w-full text-left px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors"
                          style={active ? { background: `${color}22`, color, border: `1.5px solid ${color}` } : { background: 'white', color: '#5C6670', border: '1.5px solid #E3DDD1' }}
                        >
                          {targetLabel(t, parties)}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#9AA3AB] mt-2">
                    {activeTarget.kind === 'sender' && 'Fields you fill in after every party is done'}
                    {activeTarget.kind === 'prefill' && "Fields you fill in yourself before sending — label the field with its name (e.g. \"Company Name\"); the same name anywhere else auto-fills."}
                    {activeTarget.kind === 'party' && 'Fields this recipient fills in'}
                  </p>
                </div>
                <p className="text-xs text-[#9AA3AB] mt-3">Click on the document to place a field</p>
              </div>

              {/* Placed fields list */}
              <div className="flex-1 overflow-y-auto p-4">
                {fields.length === 0 ? (
                  <p className="text-xs text-[#9AA3AB]">No fields placed yet</p>
                ) : (
                  <div className="space-y-1.5">
                    {allTargets.map(t => {
                      const tFields = fields.filter(f => targetsEqual(f.target, t));
                      if (!tFields.length) return null;
                      const color = targetColor(t, parties);
                      return (
                        <div key={targetKey(t)}>
                          <p className="text-xs font-semibold uppercase tracking-wide mb-1.5 mt-2" style={{ color }}>
                            {targetLabel(t, parties)} ({tFields.length})
                          </p>
                          {tFields.map((f) => {
                            const cfg = fieldCfg[f.type];
                            return (
                              <div key={f.id} className="bg-white rounded-md px-2.5 py-1.5 border border-gray-100 text-xs mb-1">
                                <div className="flex items-center justify-between mb-1">
                                  <span style={{ color }} className="font-medium flex items-center gap-1">
                                    <cfg.Icon className="w-3 h-3" />
                                    {cfg.label} · p{f.page}
                                  </span>
                                  <button onClick={() => removeField(f.id)} className="text-[#9AA3AB] hover:text-red-400">
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                                <input
                                  type="text"
                                  value={f.label}
                                  onChange={e => setFields(prev => prev.map(ff => ff.id === f.id ? { ...ff, label: e.target.value } : ff))}
                                  placeholder={f.target.kind === 'prefill' ? 'Parameter name (e.g. Company Name)…' : 'Field label…'}
                                  className="w-full text-xs border border-gray-200 rounded px-1.5 py-0.5 text-[#1E2A33] focus:outline-none focus:border-[#A62B1F]"
                                />
                                {f.target.kind === 'prefill' && !f.label && (
                                  <div className="flex gap-1 mt-1">
                                    {['Client Name', 'Company'].map((suggestion) => (
                                      <button
                                        key={suggestion}
                                        type="button"
                                        onClick={() => setFields(prev => prev.map(ff => ff.id === f.id ? { ...ff, label: suggestion } : ff))}
                                        className="text-[10px] px-1.5 py-0.5 rounded-full border border-gray-200 text-[#5C6670] hover:border-[#A62B1F] hover:text-[#A62B1F] transition-colors"
                                      >
                                        {suggestion}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* PDF canvas area */}
            <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
              {pdfLoading ? (
                <div className="flex items-center justify-center h-full gap-2 text-[#5C6670]">
                  <Loader2 className="w-5 h-5 animate-spin" /> Loading PDF…
                </div>
              ) : pdfError ? (
                <div className="flex flex-col items-center justify-center h-full text-center gap-3">
                  <p className="text-[#5C6670] text-sm font-medium">Could not render PDF preview</p>
                  <p className="text-[#9AA3AB] text-xs max-w-xs">The document may not be a valid PDF, or there was a network error. You can still proceed to send — the signer will see the document on the signing page.</p>
                  <Button onClick={goToDetailsStep} className="bg-[#A62B1F] hover:bg-[#7a54e0] text-white mt-2">
                    Continue without placing fields
                  </Button>
                </div>
              ) : (
                <div className="space-y-6 flex flex-col items-center">
                  {pages.map((pg) => {
                    const pageFields = fields.filter(f => f.page === pg.pageNum);
                    return (
                      <div key={pg.pageNum} className="relative shadow-md rounded overflow-hidden bg-white"
                        style={{ width: '100%', maxWidth: pg.width, cursor: 'crosshair' }}
                        ref={el => { pageRefs.current[pg.pageNum] = el; }}
                        onClick={(e) => handlePageClick(e, pg.pageNum)}
                      >
                        <canvas
                          ref={el => { if (el) { el.width = pg.canvas.width; el.height = pg.canvas.height; el.getContext('2d').drawImage(pg.canvas, 0, 0); } }}
                          style={{ display: 'block', width: '100%', height: 'auto' }}
                        />
                        {pageFields.map(f => {
                          const cfg = fieldCfg[f.type];
                          const color = targetColor(f.target, parties);
                          return (
                            <div
                              key={f.id}
                              onMouseDown={(e) => startDrag(e, f.id, 'move', pg.pageNum)}
                              style={{
                                position: 'absolute',
                                left: `${f.x_pct}%`,
                                top: `${f.y_pct}%`,
                                width: `${f.w_pct || cfg.w}%`,
                                height: `${f.h_pct || cfg.h}%`,
                                border: `2px dashed ${color}`,
                                background: `${color}22`,
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'move',
                                fontSize: 11,
                                fontWeight: 600,
                                color,
                                gap: 4,
                                userSelect: 'none',
                              }}
                            >
                              <cfg.Icon style={{ width: 12, height: 12, flexShrink: 0 }} />
                              {f.label || cfg.label}
                              <button
                                onMouseDown={e => e.stopPropagation()}
                                onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                                style={{ position: 'absolute', top: -8, right: -8, background: '#fff', border: `1px solid ${color}`, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                              >
                                <X style={{ width: 10, height: 10, color }} />
                              </button>
                              <div
                                onMouseDown={(e) => startDrag(e, f.id, 'resize', pg.pageNum)}
                                style={{ position: 'absolute', bottom: 0, right: 0, width: 12, height: 12, cursor: 'se-resize', background: color, borderRadius: '2px 0 2px 0', opacity: 0.8 }}
                              />
                            </div>
                          );
                        })}
                        <div className="absolute bottom-1 right-2 text-xs text-gray-400 pointer-events-none">
                          Page {pg.pageNum}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Step 1.5: prefill details ── */}
        {step === 1.5 && (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 w-full max-w-md">
              <h3 className="font-semibold text-[#1E2A33] text-lg mb-1">Fill in your details</h3>
              <p className="text-sm text-[#5C6670] mb-6">These values will be filled into the document before it's sent.</p>
              <div className="space-y-4">
                {prefillParams.map(name => (
                  <div key={name}>
                    <Label className="mb-1.5 block">{name} <span className="text-red-500">*</span></Label>
                    <Input
                      value={prefillValues[name] || ''}
                      onChange={e => setPrefillValues(prev => ({ ...prev, [name]: e.target.value }))}
                      placeholder={name}
                      autoFocus={name === prefillParams[0]}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: recipient details ── */}
        {step === 2 && (
          <div className="flex-1 overflow-y-auto bg-gray-50 flex items-center justify-center py-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 w-full max-w-lg">
              <h3 className="font-semibold text-[#1E2A33] text-lg mb-1">Recipient details</h3>
              <p className="text-sm text-[#5C6670] mb-6">
                Every recipient gets an email with a secure link, all at once — there's no signing order.
              </p>
              <div className="space-y-4">
                {parties.map((p, i) => (
                  <div key={p.index} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded" style={{ background: `${PARTY_COLORS[p.index % PARTY_COLORS.length]}22`, color: PARTY_COLORS[p.index % PARTY_COLORS.length] }}>
                        Party {i + 1} · {p.role === 'reviewer' ? 'Reviewer' : 'Signer'}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="mb-1.5 block text-xs">Email <span className="text-red-500">*</span></Label>
                        <Input type="email" value={p.email} onChange={e => updateParty(p.index, { email: e.target.value })} placeholder="email@example.com" />
                      </div>
                      <div>
                        <Label className="mb-1.5 block text-xs text-[#5C6670] font-normal">Name <span className="text-[10px]">(optional)</span></Label>
                        <Input value={p.name} onChange={e => updateParty(p.index, { name: e.target.value })} placeholder="Jane Doe" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-5 border-t border-gray-100">
                <Label className="mb-1.5 block text-sm font-semibold text-[#1E2A33]">Message <span className="text-[10px] font-normal text-[#5C6670]">(optional)</span></Label>
                <p className="text-xs text-[#5C6670] mb-2">Included in the email with the signing link.</p>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Add a note for your recipients…"
                  rows={4}
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm text-[#1E2A33] placeholder:text-[#9AA3AB] focus:outline-none focus:ring-1 focus:ring-[#A62B1F] resize-y"
                />
              </div>

              {hasSenderFields && (
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <h4 className="font-semibold text-[#1E2A33] text-sm mb-1">Who executes "My Part"?</h4>
                  <p className="text-xs text-[#5C6670] mb-3">This document has fields for your side too. By default you'll be notified to fill them in once everyone else is done — or hand that off to someone else.</p>
                  <div className="flex rounded-md border border-gray-200 overflow-hidden text-xs font-medium mb-3">
                    <button
                      onClick={() => setExecuteSelf(true)}
                      className={`flex-1 py-1.5 ${executeSelf ? 'bg-[#1E2A33] text-white' : 'bg-white text-[#5C6670]'}`}
                    >I'll execute it myself</button>
                    <button
                      onClick={() => setExecuteSelf(false)}
                      className={`flex-1 py-1.5 ${!executeSelf ? 'bg-[#1E2A33] text-white' : 'bg-white text-[#5C6670]'}`}
                    >Someone else executes</button>
                  </div>
                  {!executeSelf && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="mb-1.5 block text-xs">Email <span className="text-red-500">*</span></Label>
                        <Input type="email" value={executorEmail} onChange={e => setExecutorEmail(e.target.value)} placeholder="email@example.com" />
                      </div>
                      <div>
                        <Label className="mb-1.5 block text-xs text-[#5C6670] font-normal">Name <span className="text-[10px]">(optional)</span></Label>
                        <Input value={executorName} onChange={e => setExecutorName(e.target.value)} placeholder="Jane Doe" />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
